import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomModal from '../../../../configuracion/components/customModal';
import CurrencyChangeRow from '../../../../configuracion/components/CurrencyChangeRow';
import '../../../../configuracion/components/CurrencyChangeRow.scss';
import CLPTransactionExchangeForm from './CLPTransactionExchangeForm';
import { importAlertState } from '../../../../../redux/actions/alert';
import { GenerateMultipleMatch } from '../../../../../graphql/conciliacion';
import { setUpdateDocuments } from '../../../../../redux/actions/documentostributarios';
import { importUpdateTransactions } from '../../../../../redux/actions/conciliacion';
import { Button } from 'semantic-ui-react';
import { formatMoneyWithCurrency } from '../../../../../utils/formatter';
import { UserContext } from '../../../../../context';
import DocumentInfoBar from './documentInfoBar';
import { GetIndicatorByDate } from '../../../../../graphql/indicators';
import moment from 'moment';

const roundOriginalAmount = (amount) => {
  const parsed = Math.max(0, parseFloat(amount) || 0);
  return Math.round(parsed * 100) / 100;
};

const formatOriginalAmountInput = (amount) => {
  const rounded = roundOriginalAmount(amount);
  if (!rounded) return '';
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
};

const capOriginalAmount = (amount, matchAmount) => {
  const parsed = roundOriginalAmount(amount);
  const cap = roundOriginalAmount(matchAmount);
  if (cap > 0 && parsed > 0) {
    return Math.min(parsed, cap);
  }
  return parsed;
};

/** USD transaction → CLP document: CLP (localValue) is authoritative; USD is derived. */
const resolveUsdTxClpDocAmounts = ({
  documentRemainingCLP,
  transactionRemainingUsd,
  exchangeRate,
}) => {
  const rate = parseFloat(exchangeRate) || 0;
  const docClp = Math.max(0, Math.round(documentRemainingCLP || 0));
  const txUsd = roundOriginalAmount(transactionRemainingUsd);
  const txCapClp = rate > 0 ? Math.round(txUsd * rate) : docClp;
  const localAmount = Math.min(docClp, txCapClp);
  const derivedUsd = rate > 0 && localAmount > 0
    ? roundOriginalAmount(localAmount / rate)
    : 0;
  return {
    localAmount,
    originalAmount: capOriginalAmount(derivedUsd, txUsd),
  };
};

const ExchangeMatchModal = ({ 
  open, 
  onClose, 
  document, 
  data, 
  matchAmount,
  onConfirm,
  isBulkMatch = false,
  matchLines = [],
  totalLocalCLP = 0,
  closeDifferenceWithObligation = false,
  differenceCLP = 0,
}) => {
  const dispatch = useDispatch();
  const { selectedCompany: { id: companyId } } = useSelector((state) => state.companyList);
  const { executeMutation: generateMultipleMatch } = GenerateMultipleMatch();
  const userInfo = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultOriginalAmount, setDefaultOriginalAmount] = useState(0);
  const [originalAmountInput, setOriginalAmountInput] = useState('');
  const [formData, setFormData] = useState({
    invoiceCurrency: '',
    invoiceExchangeRate: '',
    originalAmount: 0,
    localAmount: 0,
    comment: ''
  });
  const initSessionRef = useRef(null);
  const userEditedAmountRef = useRef(false);
  const apiRateAppliedRef = useRef(false);

  // Check if data is a Transaction or a Document
  const isTransaction = data?.type === 'RECIEVED' || data?.type === 'SPEND';
  const transactionCurrency = isTransaction ? (data?.currency || 'CLP') : (data?.currencyType || 'CLP');
  const documentCurrency = document?.currencyType || 'CLP';
  const isUsdTxClpDoc = isTransaction && transactionCurrency !== 'CLP' && documentCurrency === 'CLP';
  const dataId = data?._id || data?.id;
  const documentId = document?._id || document?.id;
  const dataRemaining = isTransaction ? (data?.remaining ?? 0) : (data?.meta?.remaining ?? 0);
  const transactionExchangeRateValue = data?.rate?.exchange
    ? (typeof data.rate.exchange === 'number'
        ? data.rate.exchange
        : data.rate.exchange[transactionCurrency])
    : null;

  const initSessionKey = useMemo(() => {
    if (!open || !dataId) return null;
    if (isBulkMatch && matchLines?.length > 0) {
      const linesKey = matchLines.map((line) => `${line.docId}:${line.amountCLP}`).join(',');
      return `${dataId}:bulk:${linesKey}:${totalLocalCLP}`;
    }
    if (!documentId) return null;
    return `${dataId}:${documentId}:${matchAmount ?? 0}:${dataRemaining}`;
  }, [open, dataId, documentId, matchAmount, dataRemaining, isBulkMatch, matchLines, totalLocalCLP]);
  // Get transaction date (prefer stringDate for formatting)
  const transactionDate = data?.stringDate || data?.date;
  
  // Calculate currency date for API call - use ONLY transaction date
  const currencyDate = transactionDate
    ? (data?.stringDate 
        ? transactionDate // Already in DD/MM/YYYY format
        : moment(transactionDate).format("DD/MM/YYYY")) // Convert ISO to DD/MM/YYYY
    : moment().format("DD/MM/YYYY");
  
  // Fetch exchange rate indicators when transaction or document is not in CLP
  const shouldFetchExchangeRate = (isTransaction && transactionCurrency && transactionCurrency !== 'CLP') || 
                                   (documentCurrency && documentCurrency !== 'CLP');
  const currencyToFetch = transactionCurrency !== 'CLP' ? transactionCurrency : documentCurrency;
  const getIndicatorByDate = GetIndicatorByDate(shouldFetchExchangeRate ? currencyDate : null);

  useEffect(() => {
    if (!open) {
      initSessionRef.current = null;
      userEditedAmountRef.current = false;
      apiRateAppliedRef.current = false;
      setOriginalAmountInput('');
    }
  }, [open]);

  const applyInitialFormState = ({
    invoiceCurrency,
    invoiceExchangeRate,
    originalAmount,
    localAmount,
    capUsdAt,
  }) => {
    userEditedAmountRef.current = false;
    const usdCap = capUsdAt ?? matchAmount;
    const cappedOriginal = capOriginalAmount(originalAmount, usdCap);
    const rate = parseFloat(invoiceExchangeRate) || 1;
    const cappedLocal = localAmount != null && localAmount > 0
      ? Math.round(localAmount)
      : (rate > 0 ? Math.round(rate * cappedOriginal) : 0);

    setDefaultOriginalAmount(cappedOriginal);
    setOriginalAmountInput(formatOriginalAmountInput(cappedOriginal));
    setFormData({
      invoiceCurrency: invoiceCurrency || '',
      invoiceExchangeRate: invoiceExchangeRate?.toString() || '',
      originalAmount: cappedOriginal,
      localAmount: cappedLocal,
      comment: '',
    });
  };

  // Initialize form once per modal session (transaction + document + matchAmount + remaining)
  useEffect(() => {
    if (!open || !initSessionKey) return;
    if (initSessionRef.current === initSessionKey) return;

    if (isBulkMatch && totalLocalCLP > 0) {
      const originalCurrency = transactionCurrency !== 'CLP' ? transactionCurrency : 'USD';
      const transactionExchangeRate = transactionExchangeRateValue;
      const apiExchangeRate = !transactionExchangeRate
        ? (getIndicatorByDate?.data?.getIndicatorByDate?.exchange?.[originalCurrency])
        : null;
      const exchangeRate = transactionExchangeRate || apiExchangeRate || 1;
      const calculatedOriginalAmount = exchangeRate > 0
        ? parseFloat((totalLocalCLP / exchangeRate).toFixed(2))
        : 0;

      initSessionRef.current = initSessionKey;
      applyInitialFormState({
        invoiceCurrency: originalCurrency,
        invoiceExchangeRate: exchangeRate,
        originalAmount: calculatedOriginalAmount,
        localAmount: totalLocalCLP,
        capUsdAt: dataRemaining,
      });
      return;
    }

    const documentOriginalCurrency = document?.meta?.exchange?.originalCurrency || documentCurrency;
    const shouldUseMinCalculation = document?.meta?.exchange &&
      isTransaction &&
      transactionCurrency === 'CLP' &&
      documentOriginalCurrency !== 'CLP';

    if (shouldUseMinCalculation) {
      const exchange = document.meta.exchange;
      const transactionRemaining = dataRemaining;
      const documentRemaining = document?.meta?.remaining || document?.amount || 0;

      if (transactionRemaining <= 0) return;

      const minRemaining = Math.min(transactionRemaining, documentRemaining);
      const exchangeRate = exchange.exchangeRate || 1;
      const calculatedOriginalAmount = exchangeRate > 0 && minRemaining > 0
        ? parseFloat((minRemaining / exchangeRate).toFixed(2))
        : 0;

      initSessionRef.current = initSessionKey;
      applyInitialFormState({
        invoiceCurrency: exchange.originalCurrency,
        invoiceExchangeRate: exchange.exchangeRate,
        originalAmount: calculatedOriginalAmount,
        localAmount: minRemaining,
      });
      return;
    }

    if (document?.meta?.exchange) {
      const exchange = document.meta.exchange;
      const remaining = document?.meta?.remaining || 0;
      const exchangeRate = exchange.exchangeRate || 1;
      const docOriginalAmount = exchangeRate > 0
        ? parseFloat((remaining / exchangeRate).toFixed(2))
        : (exchange.originalAmount || 0);

      let calculatedOriginalAmount = docOriginalAmount;
      let calculatedLocalAmount = Math.round(document?.meta?.remaining || matchAmount || 0);
      if (isTransaction && transactionCurrency !== 'CLP') {
        const fxAmounts = resolveUsdTxClpDocAmounts({
          documentRemainingCLP: document?.meta?.remaining || matchAmount || 0,
          transactionRemainingUsd: dataRemaining,
          exchangeRate: exchange.exchangeRate || 1,
        });
        calculatedOriginalAmount = fxAmounts.originalAmount;
        calculatedLocalAmount = fxAmounts.localAmount;
      }

      applyInitialFormState({
        invoiceCurrency: exchange.originalCurrency,
        invoiceExchangeRate: exchange.exchangeRate,
        originalAmount: calculatedOriginalAmount,
        localAmount: calculatedLocalAmount,
      });
      initSessionRef.current = initSessionKey;
      return;
    }

    if ((isTransaction && transactionCurrency && transactionCurrency !== 'CLP') ||
        (documentCurrency && documentCurrency !== 'CLP')) {
      const originalCurrency = transactionCurrency !== 'CLP' ? transactionCurrency : documentCurrency;
      const documentRemaining = document?.meta?.remaining || 0;
      const documentAmount = document?.amount || 0;
      const remaining = documentRemaining || documentAmount;

      const documentMetaExchangeRate = !transactionExchangeRateValue
        ? (document?.meta?.exchange?.exchangeRate ||
           document?.meta?.exchangeRate ||
           document?.meta?.rate)
        : null;

      const apiExchangeRate = (!transactionExchangeRateValue && !documentMetaExchangeRate)
        ? (getIndicatorByDate?.data?.getIndicatorByDate?.exchange?.[originalCurrency])
        : null;

      const exchangeRate = transactionExchangeRateValue || documentMetaExchangeRate || apiExchangeRate || 1;

      let calculatedOriginalAmount = remaining;
      let calculatedLocalAmount = remaining;

      if (transactionCurrency !== 'CLP' && documentCurrency === 'CLP') {
        const docRemainingCLP = Math.round(matchAmount || remaining);
        const fxAmounts = resolveUsdTxClpDocAmounts({
          documentRemainingCLP: docRemainingCLP,
          transactionRemainingUsd: dataRemaining,
          exchangeRate,
        });
        calculatedOriginalAmount = fxAmounts.originalAmount;
        calculatedLocalAmount = fxAmounts.localAmount;
      } else if (transactionCurrency === 'CLP' && documentCurrency !== 'CLP') {
        const transactionRemaining = dataRemaining;
        const docRemaining = document?.meta?.remaining || document?.amount || 0;
        const minRemaining = Math.min(transactionRemaining, docRemaining);
        const exchangeRateForCalculation = document?.meta?.exchange?.exchangeRate || exchangeRate;
        calculatedOriginalAmount = exchangeRateForCalculation > 0 && minRemaining > 0
          ? parseFloat((minRemaining / exchangeRateForCalculation).toFixed(2))
          : 0;
        calculatedLocalAmount = minRemaining;
      } else {
        calculatedLocalAmount = exchangeRate > 0 ? Math.round(exchangeRate * remaining) : remaining;
      }

      applyInitialFormState({
        invoiceCurrency: originalCurrency,
        invoiceExchangeRate: exchangeRate,
        originalAmount: calculatedOriginalAmount,
        localAmount: calculatedLocalAmount,
      });
      initSessionRef.current = initSessionKey;
      return;
    }

    initSessionRef.current = initSessionKey;
    setDefaultOriginalAmount(0);
    setOriginalAmountInput('');
    setFormData({
      invoiceCurrency: '',
      invoiceExchangeRate: '',
      originalAmount: 0,
      localAmount: 0,
      comment: '',
    });
  }, [
    open,
    initSessionKey,
    document,
    isTransaction,
    transactionCurrency,
    documentCurrency,
    dataRemaining,
    transactionExchangeRateValue,
    matchAmount,
    getIndicatorByDate?.data?.getIndicatorByDate?.exchange,
    isBulkMatch,
    totalLocalCLP,
  ]);

  // Fill exchange rate from API once per session, before the user edits amounts
  useEffect(() => {
    if (!open || !initSessionKey || isBulkMatch || document?.meta?.exchange || userEditedAmountRef.current) return;
    if (apiRateAppliedRef.current) return;
    if (initSessionRef.current !== initSessionKey) return;

    const originalCurrency = transactionCurrency !== 'CLP' ? transactionCurrency : documentCurrency;
    const documentMetaExchangeRate = document?.meta?.exchangeRate ||
      document?.meta?.exchange?.exchangeRate ||
      document?.meta?.rate ||
      null;

    if (transactionExchangeRateValue || documentMetaExchangeRate) return;

    const apiExchangeRate = getIndicatorByDate?.data?.getIndicatorByDate?.exchange?.[originalCurrency];
    if (!apiExchangeRate || apiExchangeRate <= 0) return;

    const documentRemaining = document?.meta?.remaining || 0;
    const documentAmount = document?.amount || 0;
    const remaining = documentRemaining || documentAmount;
    if (remaining <= 0) return;

    let calculatedOriginalAmount = remaining;
    let calculatedLocalAmount = remaining;

    if (transactionCurrency !== 'CLP' && documentCurrency === 'CLP') {
      const docRemainingCLP = Math.round(matchAmount || remaining);
      const fxAmounts = resolveUsdTxClpDocAmounts({
        documentRemainingCLP: docRemainingCLP,
        transactionRemainingUsd: dataRemaining,
        exchangeRate: apiExchangeRate,
      });
      calculatedOriginalAmount = fxAmounts.originalAmount;
      calculatedLocalAmount = fxAmounts.localAmount;
    } else if (transactionCurrency === 'CLP' && documentCurrency !== 'CLP') {
      const transactionRemaining = dataRemaining;
      const docRemaining = document?.meta?.remaining || document?.amount || 0;
      const minRemaining = Math.min(transactionRemaining, docRemaining);
      const exchangeRateForCalculation = document?.meta?.exchange?.exchangeRate || apiExchangeRate;
      calculatedOriginalAmount = exchangeRateForCalculation > 0 && minRemaining > 0
        ? parseFloat((minRemaining / exchangeRateForCalculation).toFixed(2))
        : 0;
      calculatedLocalAmount = minRemaining;
    }

    const cappedOriginal = capOriginalAmount(calculatedOriginalAmount, matchAmount);
    const cappedLocal = isUsdTxClpDoc
      ? calculatedLocalAmount
      : Math.round(apiExchangeRate * cappedOriginal);

    setDefaultOriginalAmount(cappedOriginal);
    setOriginalAmountInput(formatOriginalAmountInput(cappedOriginal));
    setFormData((prev) => ({
      ...prev,
      invoiceExchangeRate: apiExchangeRate.toString(),
      originalAmount: cappedOriginal,
      localAmount: cappedLocal,
    }));
    apiRateAppliedRef.current = true;
  }, [
    open,
    initSessionKey,
    document,
    transactionCurrency,
    documentCurrency,
    dataRemaining,
    transactionExchangeRateValue,
    matchAmount,
    getIndicatorByDate?.data?.getIndicatorByDate?.exchange,
  ]);

  // Bulk match: refresh USD amount when API exchange rate arrives
  useEffect(() => {
    if (!open || !initSessionKey || !isBulkMatch || !totalLocalCLP) return;
    if (apiRateAppliedRef.current) return;
    if (initSessionRef.current !== initSessionKey) return;
    if (transactionExchangeRateValue) return;

    const originalCurrency = transactionCurrency !== 'CLP' ? transactionCurrency : 'USD';
    const apiExchangeRate = getIndicatorByDate?.data?.getIndicatorByDate?.exchange?.[originalCurrency];
    if (!apiExchangeRate || apiExchangeRate <= 0) return;

    const calculatedOriginalAmount = parseFloat((totalLocalCLP / apiExchangeRate).toFixed(2));
    const cappedOriginal = capOriginalAmount(calculatedOriginalAmount, dataRemaining);

    setDefaultOriginalAmount(cappedOriginal);
    setOriginalAmountInput(formatOriginalAmountInput(cappedOriginal));
    setFormData((prev) => ({
      ...prev,
      invoiceExchangeRate: apiExchangeRate.toString(),
      originalAmount: cappedOriginal,
      localAmount: totalLocalCLP,
    }));
    apiRateAppliedRef.current = true;
  }, [
    open,
    initSessionKey,
    isBulkMatch,
    totalLocalCLP,
    transactionCurrency,
    dataRemaining,
    transactionExchangeRateValue,
    getIndicatorByDate?.data?.getIndicatorByDate?.exchange,
  ]);

  // Re-execute exchange rate query when currency date changes or currency changes
  useEffect(() => {
    if (shouldFetchExchangeRate && currencyDate && open && currencyToFetch) {
      getIndicatorByDate.reexecuteQuery({ requestPolicy: 'network-only' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchExchangeRate, currencyDate, currencyToFetch, open]);

  const handleInvoiceCurrencyChange = (value) => {
    setFormData(prev => ({
      ...prev,
      invoiceCurrency: value
    }));
  };

  const handleInvoiceExchangeRateChange = (e) => {
    const value = e.target.value;
    // Allow up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      const exchangeRate = parseFloat(value) || 0;
      
      // Check if this is CLP transaction + non-CLP document case
      const documentOriginalCurrency = document?.meta?.exchange?.originalCurrency || documentCurrency;
      const isCLPTransactionCase = isTransaction && transactionCurrency === 'CLP' && documentOriginalCurrency !== 'CLP';
      
      if (isCLPTransactionCase) {
        // For CLP transaction case: originalAmount = localAmount / exchangeRate
        const localAmount = formData.localAmount || 0;
        const calculatedOriginalAmount = exchangeRate > 0 && localAmount > 0
          ? parseFloat((localAmount / exchangeRate).toFixed(2))
          : 0;
        setFormData(prev => ({
          ...prev,
          invoiceExchangeRate: value,
          originalAmount: calculatedOriginalAmount
        }));
      } else if (isUsdTxClpDoc) {
        const localAmount = formData.localAmount || 0;
        const calculatedOriginalAmount = exchangeRate > 0 && localAmount > 0
          ? capOriginalAmount(parseFloat((localAmount / exchangeRate).toFixed(2)), dataRemaining)
          : 0;
        setOriginalAmountInput(formatOriginalAmountInput(calculatedOriginalAmount));
        setFormData(prev => ({
          ...prev,
          invoiceExchangeRate: value,
          originalAmount: calculatedOriginalAmount,
          localAmount,
        }));
      } else if (isBulkMatch && totalLocalCLP > 0) {
        const calculatedOriginalAmount = exchangeRate > 0
          ? parseFloat((totalLocalCLP / exchangeRate).toFixed(2))
          : 0;
        const cappedOriginal = capOriginalAmount(calculatedOriginalAmount, dataRemaining);
        setOriginalAmountInput(formatOriginalAmountInput(cappedOriginal));
        setDefaultOriginalAmount(cappedOriginal);
        setFormData(prev => ({
          ...prev,
          invoiceExchangeRate: value,
          originalAmount: cappedOriginal,
          localAmount: totalLocalCLP,
        }));
      } else {
        // For other cases: localAmount = exchangeRate * originalAmount
        const originalAmount = formData.originalAmount || 0;
        const localAmount = exchangeRate > 0 ? Math.round(exchangeRate * originalAmount) : 0;
        setFormData(prev => ({
          ...prev,
          invoiceExchangeRate: value,
          localAmount: localAmount
        }));
      }
    }
  };

  const handleOriginalAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      userEditedAmountRef.current = true;
      setOriginalAmountInput(value);

      const numValue = parseFloat(value) || 0;

      if (value !== '' && numValue > defaultOriginalAmount) {
        const exchangeRate = parseFloat(formData.invoiceExchangeRate) || 1;
        const capped = defaultOriginalAmount;
        const localAmount = exchangeRate > 0 ? Math.round(exchangeRate * capped) : 0;
        setOriginalAmountInput(formatOriginalAmountInput(capped));
        setFormData((prev) => ({
          ...prev,
          originalAmount: capped,
          localAmount,
        }));
        return;
      }

      if (value === '' || numValue >= 0) {
        const exchangeRate = parseFloat(formData.invoiceExchangeRate) || 1;
        const roundedValue = roundOriginalAmount(numValue);
        const localAmount = exchangeRate > 0 ? Math.round(exchangeRate * roundedValue) : 0;
        setFormData((prev) => ({
          ...prev,
          originalAmount: value === '' ? 0 : roundedValue,
          localAmount,
        }));
      }
    }
  };

  const handleCommentChange = (e) => {
    setFormData(prev => ({
      ...prev,
      comment: e.target.value
    }));
  };

  const handleLocalAmountChange = (e) => {
    const value = e.target.value;
    // Allow decimals up to 2 places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      const numValue = parseFloat(value) || 0;
      const transactionRemaining = isTransaction ? (data?.remaining || 0) : (data?.meta?.remaining || 0);
      
      // If the value exceeds the transaction remaining, revert to transaction remaining
      if (value !== '' && numValue > transactionRemaining) {
        const exchangeRate = parseFloat(formData.invoiceExchangeRate) || 1;
        const calculatedOriginalAmount = exchangeRate > 0 && transactionRemaining > 0
          ? parseFloat((transactionRemaining / exchangeRate).toFixed(2))
          : 0;
        setFormData(prev => ({
          ...prev,
          localAmount: transactionRemaining,
          originalAmount: calculatedOriginalAmount
        }));
        return;
      }
      
      // Validate: not lower than 0
      if (value === '' || numValue >= 0) {
        const exchangeRate = parseFloat(formData.invoiceExchangeRate) || 1;
        const calculatedOriginalAmount = exchangeRate > 0 && numValue > 0
          ? parseFloat((numValue / exchangeRate).toFixed(2))
          : 0;
        setFormData(prev => ({
          ...prev,
          localAmount: value === '' ? 0 : numValue,
          originalAmount: calculatedOriginalAmount
        }));
      }
    }
  };

  const handleConfirm = async () => {
    if (!formData.invoiceCurrency) {
      dispatch(
        importAlertState({
          content: 'Por favor seleccione una moneda.',
          state: 'error',
          duration: 4,
        })
      );
      return;
    }

    if (!formData.invoiceExchangeRate || parseFloat(formData.invoiceExchangeRate) <= 0) {
      dispatch(
        importAlertState({
          content: 'Por favor ingrese un tipo de cambio válido.',
          state: 'error',
          duration: 4,
        })
      );
      return;
    }

    setIsLoading(true);

    const exchangeRate = parseFloat(formData.invoiceExchangeRate) || 1;
    const dataCurrency = isTransaction ? data.currency : data.currencyType;

    if (isBulkMatch && matchLines.length > 0) {
      const usdTotal = roundOriginalAmount(parseFloat(originalAmountInput) || parseFloat(formData.originalAmount) || 0);
      const impliedLocalCLP = Math.round(exchangeRate * usdTotal);
      const transactionCapCLP = Math.round((data?.remaining || 0) * exchangeRate);

      if (!usdTotal) {
        dispatch(importAlertState({ content: 'El monto por conciliar debe ser mayor a 0.', state: 'error', duration: 4 }));
        setIsLoading(false);
        return;
      }

      if (totalLocalCLP > transactionCapCLP) {
        dispatch(importAlertState({
          content: `El total seleccionado (${formatMoneyWithCurrency(totalLocalCLP, 'CLP')}) supera el saldo del movimiento (${formatMoneyWithCurrency(transactionCapCLP, 'CLP')}).`,
          state: 'error',
          duration: 5,
        }));
        setIsLoading(false);
        return;
      }

      // USD se guarda con 2 decimales, así que el round-trip CLP → USD(2 dec) → CLP
      // nunca reproduce exactamente el total en pesos: el error puede llegar a
      // tipoDeCambio × 0,005. La tolerancia debe contemplar esa granularidad.
      const roundTripToleranceCLP = Math.ceil(exchangeRate / 100) + 1;
      if (Math.abs(impliedLocalCLP - totalLocalCLP) > roundTripToleranceCLP) {
        dispatch(importAlertState({
          content: 'El monto en moneda original y el tipo de cambio no coinciden con el total en pesos seleccionado.',
          state: 'error',
          duration: 5,
        }));
        setIsLoading(false);
        return;
      }

      let innerComments = null;
      if (formData.comment && formData.comment.trim()) {
        innerComments = [{
          transactionId: data?.id,
          user: {
            id: userInfo?.id,
            name: userInfo?.name,
            email: userInfo?.email,
          },
          date: new Date(),
          comment: formData.comment.trim(),
        }];
      }

      const originInput = {
        originId: data?._id || data?.id,
        originType: 'TRANSACTION',
        currency: dataCurrency || 'CLP',
        originalAmount: usdTotal,
        amount: usdTotal,
      };
      if (innerComments) {
        originInput.innerComments = innerComments;
      }

      const end = matchLines.map((line) => {
        const doc = line.document;
        const lineUsdAmount = exchangeRate > 0
          ? parseFloat((line.amountCLP / exchangeRate).toFixed(2))
          : 0;
        const endInput = {
          endId: doc?._id || doc?.id,
          endType: doc?.documentType,
          currency: 'CLP',
          originalAmount: lineUsdAmount,
          amount: line.amountCLP,
          exchangeRate,
          localValue: line.amountCLP,
          matchCurrency: 'CLP',
          paymentDate: doc?.documentDate || new Date(),
        };
        return endInput;
      });

      const normalizeOblType = (t) =>
        String(t || '').toLowerCase().replace(/_/g, '');
      const isTraspasoFxRendicion =
        normalizeOblType(document?.meta?.type) === 'rendicionportraspasoentrecuentas' ||
        (Array.isArray(matchLines) &&
          matchLines.some(
            (line) =>
              normalizeOblType(line?.document?.meta?.type || line?.meta?.type) ===
              'rendicionportraspasoentrecuentas',
          ));

      const input = {
        companyId: companyId || data?.companyId,
        accountPlan: 'NOPLAN',
        origin: [originInput],
        end,
        close: closeDifferenceWithObligation && differenceCLP > 0 ? {
          shouldBeClose: true,
          amount: differenceCLP,
          type: isTraspasoFxRendicion ? 'EXCHANGE_RATE_DIFFERENCE' : 'DIFFERENCE',
          isReceived: data?.type === 'SPEND',
        } : undefined,
      };

      const { error: matchError } = await generateMultipleMatch({ input });

      if (matchError) {
        let errorMessage = 'Hubo un problema al hacer el match.';
        if (matchError.graphQLErrors?.length > 0) {
          errorMessage = matchError.graphQLErrors[0].message;
        } else if (matchError.message) {
          errorMessage = matchError.message;
        }
        const isPeriodLocked = typeof errorMessage === 'string' &&
          errorMessage.toLowerCase().includes('período') &&
          errorMessage.toLowerCase().includes('cerrado');
        dispatch(importAlertState({
          concept: isPeriodLocked ? 'Período Cerrado' : undefined,
          content: errorMessage,
          state: isPeriodLocked ? 'warning' : 'error',
          duration: isPeriodLocked ? 6 : 4,
        }));
        setIsLoading(false);
        return;
      }

      dispatch(setUpdateDocuments({ value: true }));
      dispatch(importUpdateTransactions(true));
      if (onConfirm) onConfirm();
      setIsLoading(false);
      onClose();
      return;
    }

    // Check if this is CLP transaction + non-CLP document case
    const documentOriginalCurrency = document?.meta?.exchange?.originalCurrency || document?.currencyType || 'CLP';
    const isCLPTransactionCase = isTransaction && transactionCurrency === 'CLP' && documentOriginalCurrency !== 'CLP';
    
    // For CLP transaction case, use localAmount and calculate originalAmount from it
    // For other cases, use originalAmount directly
    let amount, originalAmount;
    if (isCLPTransactionCase) {
      const localAmount = Math.max(0, parseFloat(formData.localAmount) || 0);
      const exchangeRate = parseFloat(formData.invoiceExchangeRate) || 1;
      originalAmount = exchangeRate > 0 && localAmount > 0
        ? parseFloat((localAmount / exchangeRate).toFixed(2))
        : 0;
      amount = originalAmount;
    } else {
      amount = roundOriginalAmount(parseFloat(originalAmountInput) || parseFloat(formData.originalAmount) || 0);
      originalAmount = amount;
    }
    
    if (!amount || amount === 0) {
      dispatch(
        importAlertState({
          content: `El monto por conciliar debe ser mayor a 0.`,
          state: "error",
          duration: 4,
        })
      );
      setIsLoading(false);
      return;
    }

    // Build the match input exactly like TableContent.jsx match function
    const docCurrency = document?.currencyType || 'CLP';
    
    // Check if we need exchange data (either transaction or document is not CLP)
    // For CLP transaction case, we always need exchange data
    const needsExchangeData = isCLPTransactionCase || 
                              (isTransaction && dataCurrency && dataCurrency !== 'CLP') || 
                              (docCurrency && docCurrency !== 'CLP');
    
    // Get exchange rate and local value from formData
    const localValue = isCLPTransactionCase || isUsdTxClpDoc
      ? Math.round(parseFloat(formData.localAmount) || 0)
      : (needsExchangeData ? Math.round(exchangeRate * amount) : amount);

    // Build exchangeData object (same as TableContent when exchangeData is provided)
    const exchangeData = needsExchangeData ? {
      exchangeRate: exchangeRate,
      localValue: localValue
    } : null;

    // Build innerComments if comment is provided
    let innerComments = null;
    if (formData.comment && formData.comment.trim()) {
      const commentId = isTransaction ? data.id : (document?.id || data._id);
      innerComments = [{
        transactionId: commentId, // Use transactionId field even for documents (backend will handle it)
        user: {
          id: userInfo?.id,
          name: userInfo?.name,
          email: userInfo?.email,
        },
        date: new Date(),
        comment: formData.comment.trim(),
      }];
    }

    let origin, end;
    
    if (isTransaction) {
      // Transaction is ORIGIN, selected document is END
      if (isCLPTransactionCase) {
        // For CLP transaction + non-CLP document:
        // originInput.originalAmount = localAmount (CLP amount from transaction)
        // endInput.originalAmount = originalAmount (USD amount)
        // endInput.amount = localValue (CLP amount for obligation)
        const transactionLocalAmount = Math.round(parseFloat(formData.localAmount) || 0);
        const originInput = {
          originId: data?._id || data?.id,
          originType: "TRANSACTION",
          currency: dataCurrency || 'CLP',
          originalAmount: transactionLocalAmount, // Use localAmount (CLP) for transaction
          amount: transactionLocalAmount,
        };

        const endInput = {
          endId: document?._id || document?.id,
          endType: document?.documentType,
          currency: documentOriginalCurrency || 'USD', // Use document's original currency
          originalAmount: originalAmount, // USD amount
          amount: localValue // CLP amount for obligation
        };

        // Add exchange rate and local value
        if (exchangeRate && localValue) {
          endInput.exchangeRate = exchangeRate;
          endInput.localValue = localValue;
          endInput.matchCurrency = 'CLP';
          endInput.paymentDate = document?.documentDate || new Date();
        }

        // Add innerComments to the input that will receive the comment (transaction in this case)
        if (innerComments) {
          originInput.innerComments = innerComments;
        }

        origin = [originInput];
        end = [endInput];
      } else {
        // For other cases (non-CLP transaction or CLP document)
        const originInput = {
          originId: data?._id || data?.id,
          originType: "TRANSACTION",
          currency: dataCurrency || 'CLP',
          originalAmount: amount,
          amount: amount,
        };

        const docCurrencyEnd = document?.currencyType || 'CLP';
        const txNonCLP = dataCurrency && dataCurrency !== 'CLP';
        const docIsCLP = docCurrencyEnd === 'CLP';
        // Document (end) amounts must be in document currency: for CLP invoice + FX, use CLP (localValue), not USD (amount)
        const endAmountCLP =
          exchangeData && exchangeRate && localValue && txNonCLP && docIsCLP ? localValue : amount;

        const endInput = {
          endId: document?._id || document?.id,
          endType: document?.documentType,
          currency: docCurrencyEnd,
          originalAmount: endAmountCLP,
          amount: endAmountCLP,
        };

        // Add exchange rate and local value if exchangeData was provided (same logic as TableContent)
        // Exchange data goes on the document (END) side when there's a currency mismatch
        if (exchangeData && exchangeRate && localValue) {
          endInput.exchangeRate = exchangeRate;
          endInput.localValue = localValue;
          endInput.matchCurrency = 'CLP';
          endInput.paymentDate = document?.documentDate || new Date();
        }

        // Add innerComments to the input that will receive the comment (transaction in this case)
        if (innerComments) {
          originInput.innerComments = innerComments;
        }

        origin = [originInput];
        end = [endInput];
      }

    } else {
      // Document is ORIGIN, transaction is END (modal opened from document row)
      const docCurrencyOrigin = document?.currencyType || 'CLP';
      const txNonCLPEnd = dataCurrency && dataCurrency !== 'CLP';
      const docIsCLPOrigin = docCurrencyOrigin === 'CLP';
      const originAmountCLP =
        exchangeData && exchangeRate && localValue && txNonCLPEnd && docIsCLPOrigin ? localValue : amount;

      const originInput = {
        originId: document?._id || document?.id,
        originType: document?.documentType,
        currency: docCurrencyOrigin,
        originalAmount: originAmountCLP,
        amount: originAmountCLP,
      };

      const endInput = {
        endId: data?._id || data?.id,
        endType: data.documentType,
        currency: dataCurrency || 'CLP',
        originalAmount: amount,
        amount: amount,
      };

      // Add exchange rate and local value if exchangeData was provided (same logic as TableContent)
      // Exchange data goes on the document (ORIGIN) side when there's a currency mismatch
      if (exchangeData && exchangeRate && localValue) {
        originInput.exchangeRate = exchangeRate;
        originInput.localValue = localValue;
        originInput.matchCurrency = 'CLP';
        originInput.paymentDate = document?.documentDate || new Date();
      }

      // Add innerComments to the input that will receive the comment (document in this case)
      if (innerComments) {
        originInput.innerComments = innerComments;
      }

      origin = [originInput];
      end = [endInput];
    }

    const input = {
      companyId: companyId || data?.companyId || document?.companyId,
      accountPlan: "NOPLAN",
      origin,
      end
    };

    // Execute the match (same as TableContent)
    const { data: matchData, error: matchError } = await generateMultipleMatch({ input });

    if (matchError) {
      // Try multiple ways to extract the error message
      let errorMessage = "Hubo un problema al hacer el match.";
      
      if (matchError.graphQLErrors && matchError.graphQLErrors.length > 0) {
        errorMessage = matchError.graphQLErrors[0].message;
      } else if (matchError.message) {
        errorMessage = matchError.message;
      } else if (matchError.networkError?.result?.errors?.[0]?.message) {
        errorMessage = matchError.networkError.result.errors[0].message;
      }
      
      // Check if it's a period locked error
      const isPeriodLocked = errorMessage && typeof errorMessage === 'string' && 
        errorMessage.toLowerCase().includes('período') && 
        errorMessage.toLowerCase().includes('cerrado');
      
      dispatch(
        importAlertState({
          concept: isPeriodLocked ? "Período Cerrado" : undefined,
          content: errorMessage,
          state: isPeriodLocked ? "warning" : "error",
          duration: isPeriodLocked ? 6 : 4,
        })
      );
      setIsLoading(false);
      return; // Stop execution to keep modal open and show error
    }
    
    // No success toast - only show error toast if something fails
    
    // Trigger refetch to get fresh data
    dispatch(setUpdateDocuments({ value: true }));
    dispatch(importUpdateTransactions(true));
    
    if (onConfirm) {
      onConfirm({
        currency: formData.invoiceCurrency,
        exchangeRate: exchangeRate,
        localValue: localValue,
        originalAmount: amount
      });
    }
    setIsLoading(false);
    onClose();
  };

  // Only render if transaction is not CLP OR document is not CLP
  // OR if document has exchange data (invoice case)
  const shouldRender = isBulkMatch ||
                       (isTransaction && transactionCurrency && transactionCurrency !== 'CLP') ||
                       (documentCurrency && documentCurrency !== 'CLP') ||
                       document?.meta?.exchange;
  
  if (!shouldRender) {
    return null;
  }

  // Generate title based on whether data is a transaction or document
  const modalTitle = isTransaction && data
    ? (() => {
        const amount = formatMoneyWithCurrency(data.remaining ?? data.amount, data.currency);
        const date = moment(data.stringDate || data.date, data.stringDate ? 'DD/MM/YYYY' : undefined).format('DD/MM/YYYY');
        const description = data.description || data.concept || '';
        const isReceived = data.type === 'RECIEVED' || data.type === 'RECEIVED';
        const contraparte = isReceived ? (data.issuerName || data.issuer || '') : (data.receiverName || data.receiver || '');
        return `${amount} depositado el ${date}${description ? ` | ${description}` : ''}${contraparte ? ` | Contraparte: ${contraparte}` : ''}`;
      })()
    : "Configurar Tipo de Cambio";

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={modalTitle}
      content={
        <div style={{ padding: '20px' }}>
          {isBulkMatch && matchLines.length > 0 && (
            <div style={{ marginBottom: '20px', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#3C4B64', marginBottom: '8px' }}>
                {matchLines.length} documento{matchLines.length > 1 ? 's' : ''} seleccionado{matchLines.length > 1 ? 's' : ''}
              </div>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#4B5563' }}>
                {matchLines.map((line) => {
                  const doc = line.document;
                  const label = doc?.meta?.summary || doc?.meta?.typeLabel || 'Documento';
                  const folio = doc?.meta?.documentId || doc?.meta?.number;
                  return (
                    <li key={line.docId} style={{ marginBottom: '4px' }}>
                      {label}{folio ? ` #${folio}` : ''}: {formatMoneyWithCurrency(line.amountCLP, 'CLP')}
                    </li>
                  );
                })}
              </ul>
              <div style={{ marginTop: '10px', fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>
                Total en pesos: {formatMoneyWithCurrency(totalLocalCLP, 'CLP')}
              </div>
            </div>
          )}

          {/* Document Info Bar */}
          {document && !isBulkMatch && (
            <div style={{ marginBottom: '20px' }}>
              <DocumentInfoBar editData={document} />
            </div>
          )}
          
          {/* Check if this is CLP transaction + non-CLP document case */}
          {(() => {
            const documentOriginalCurrency = document?.meta?.exchange?.originalCurrency || documentCurrency;
            const isCLPTransactionCase = isTransaction && transactionCurrency === 'CLP' && documentOriginalCurrency !== 'CLP';
            const transactionRemaining = isTransaction ? (data?.remaining || 0) : (data?.meta?.remaining || 0);
            
            if (isCLPTransactionCase) {
              // Use the new CLPTransactionExchangeForm component
              return (
                <CLPTransactionExchangeForm
                  formData={formData}
                  handleLocalAmountChange={handleLocalAmountChange}
                  handleInvoiceCurrencyChange={handleInvoiceCurrencyChange}
                  handleInvoiceExchangeRateChange={handleInvoiceExchangeRateChange}
                  maxLocalAmount={transactionRemaining}
                  disabled={true}
                  disableExchangeRate={document?.meta?.exchange ? true : false}
                  exchangeRateDate={transactionDate}
                />
              );
            } else {
              // Use the original form layout
              return (
                <>
                  {/* MONTO MONEDA ORIGINAL input row */}
                  <div className="currency-change-field" style={{ marginBottom: '16px' }}>
                    <label className="currency-change-label" htmlFor="exchange-match-amount">
                      MONTO MONEDA ORIGINAL ({formData.invoiceCurrency || document?.meta?.exchange?.originalCurrency || 'USD'})
                    </label>
                    <input
                      id="exchange-match-amount"
                      type="text"
                      value={originalAmountInput}
                      onChange={handleOriginalAmountChange}
                      placeholder="0.00"
                      inputMode="decimal"
                      readOnly={isBulkMatch}
                      className={isBulkMatch ? 'currency-change-input currency-change-input-disabled' : 'currency-change-input'}
                    />
                    {isBulkMatch && (
                      <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px', fontStyle: 'italic' }}>
                        Calculado desde el total en pesos seleccionado y el tipo de cambio.
                      </div>
                    )}
                    {!isBulkMatch && defaultOriginalAmount > 0 && (
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#6B7280', 
                        marginTop: '4px',
                        fontStyle: 'italic'
                      }}>
                        El monto máximo a conciliar es {formatMoneyWithCurrency(defaultOriginalAmount, formData.invoiceCurrency || document?.meta?.exchange?.originalCurrency || 'USD')}
                      </div>
                    )}
                  </div>
                  
                  {(() => {
                    // Determine obligationCurrency for UF/UTM display
                    const originalCurrency = document?.meta?.exchange?.originalCurrency;
                    const obligationCurrency = (originalCurrency === 'UF' || originalCurrency === 'UTM') 
                      ? originalCurrency 
                      : null;
                    
                    return (
                      <CurrencyChangeRow
                        formData={formData}
                        handleInvoiceCurrencyChange={handleInvoiceCurrencyChange}
                        handleInvoiceExchangeRateChange={handleInvoiceExchangeRateChange}
                        disabled={obligationCurrency !== null || isBulkMatch}
                        disableExchangeRate={document?.meta?.exchange ? true : false}
                        obligationCurrency={obligationCurrency}
                        showReconciliationText={true}
                        exchangeRateDate={transactionDate}
                      />
                    );
                  })()}
                </>
              );
            }
          })()}

          {/* Comment field */}
          <div className="currency-change-field" style={{ marginTop: '16px' }}>
            <label className="currency-change-label" htmlFor="exchange-match-comment">
              COMENTARIOS
            </label>
            <textarea
              id="exchange-match-comment"
              className="currency-change-textarea"
              value={formData.comment || ""}
              onChange={handleCommentChange}
              placeholder="Ingrese comentarios adicionales (opcional)"
              rows={4}
            />
          </div>
        </div>
      }
      customActions={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '10px' }}>
          <Button onClick={onClose} disabled={isLoading}>Cancelar</Button>
          <Button color="blue" onClick={handleConfirm} loading={isLoading} disabled={isLoading}>
            Conciliar
          </Button>
        </div>
      }
      size="tiny"
      classNameTitle="!tw-text-center !tw-text-[16px] !tw-font-semibold !tw-px-4 !tw-py-3"
      dimmer={{ style: { zIndex: 10000, backgroundColor: 'rgba(0,0,0,0.5)' } }}
      modalStyle={{ 
        width: '1400px',
        minWidth: '80%',
        maxWidth: '90%', 
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        padding: '0'
      }}
    />
  );
};

export default ExchangeMatchModal;

